/* eslint-disable react/no-unstable-nested-components */
import * as React from "react";
import {
    Button,
    Card,
    Checkbox,
    DatePicker,
    Dialog,
    DialogContent,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    FilterButton,
    MoreButton,
    Pagination,
    Popover,
    PopoverContent,
    PopoverTrigger,
    SearchInput,
    Table,
} from "@jhool-io/fe-components";
import { ColumnDef, Row } from "@tanstack/react-table";
import { useNavigate, useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import { useIsMutating, useQueryClient } from "@tanstack/react-query";
import {
    FlaggedNoteForAuditStatus,
    INote,
    NoteLabels,
    NoteListSortAttribute,
    NoteStatus,
    NoteTypes,
} from "../types/notes.types";
import { useFetchNotesList } from "../../../hooks/queries/note";
import { CPT_CODES, LISTS_DEFAULT_LIMIT } from "../../../utils/constants";
import { useDebounce } from "../../../hooks/helpers";
import {
    convertDateFilterStringToDate,
    displayNameInRightFormat,
    formatDate,
    getTotalAmountOfTableRowsSelected,
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
    truncateString,
} from "../../../utils/helpers";
import { UserPermisions, UserRole } from "../../../utils/types/user";
import { useFetchInsuranceProviders } from "../../../hooks/queries/client";
import useShowInsuranceOverSelfPay from "../../practice-settings/hooks/useShowInsuranceOverSelfPay";
import useToast from "../../../hooks/useToast";
import {
    useChangeCptCode,
    useChangeNoteArchiveStatus,
    useExportNotes,
} from "../../../hooks/mutations/note";
import { exportNotesCsv } from "../../../services/note";
import Navbar from "../../../components/Navbar/Navbar";
import ArchiveNote from "../../../components/Notes/NoteContent/ArchiveNote/ArchiveNote";
import FlagNoteForm from "../components/FlagNoteForm/FlagNoteForm";
import ChangeCode from "../../../components/Notes/NoteContent/ChangeCode/ChangeCode";
import ChangeCodeFormPsych from "../components/ChangeCodeFornPsych/ChangeCodeFormPsych";
import ListState from "../../../components/ListState/ListState";
import TableFiltersDisplay from "../../../shared-ui/TableFiltersDisplay/TableFiltersDisplay";
import InsuranceProviderFilter, {
    getInsuranceProviderIdsFilter,
    setInsuranceProviderIdsFilter,
} from "../../../shared-ui/InsuranceProviderFilter/InsuranceProviderFilter";
import CheckPrimaryColorIcon from "../../../components/Icons/CheckPrimaryColor";
import styles from "../../../components/Notes/List/NoteListWrapper/NoteListWrapper.module.scss";
import {
    getCptCodeToDisplay,
    getFlaggedNoteStatusTag,
    getNoteStatusTag,
    getNoteTypeTextWithTag,
    getSelectedAuthorName,
    getPdfExportableNoteIds,
    isNotePdfExportable,
    isPsychNote,
    updateAuthorFilterSearchParams,
} from "../helpers/notes.helpers";
import { useExportPdfs } from "../../../hooks/queries";
import { useAppSelector } from "../../../hooks/useRedux";
import ExportLoaderAndText from "../../../shared-ui/ExportLoaderAndText/ExportLoaderAndText";
import Loader from "../../../shared-ui/Loader/Loader";
import { useFetchProviders } from "../../providers/hooks/providers.queries";
import {
    useDoesUserHavePermission,
    useDoesUserHaveRole,
} from "../../../hooks/permissions";
import { getNotesRecoveryPage } from "../helpers/pagination";

type Dialogs =
    | "archive-note"
    | "flag_note"
    | "unflag_note"
    | "change-code"
    | "loader";

const LOCAL_STORAGE_KEY = "pending_code_review_notes_filters";

export default function PendingCodeReviewNotesPage() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [dialogInView, setDialogInView] = React.useState<Dialogs | null>(
        null
    );
    const [rowSelection, setRowSelection] = React.useState({});
    const [codeToConfirmId, setCodeToConfirmId] = React.useState<string | null>(
        null
    );

    const [notesCount, setNotesCount] = React.useState("");
    const [showAuthorPopover, setShowAuthorPopover] = React.useState(false);
    const [isExportingCsv, setIsExportingCsv] = React.useState(false);
    const [providerNameSearchString, setProviderNameSearchString] =
        React.useState("");
    const [authorSearchString, setAuthorSearchString] = React.useState("");
    const [getSelectedData, setGetSelectedData] =
        React.useState<Row<INote>[]>();
    const [jobId, setJobId] = React.useState<string | null>(null);
    const [pdfCount, setPdfCount] = React.useState(0);

    const [sortConfig, setSortConfig] = React.useState<{
        key: string;
        direction: string;
    }>({
        key: searchParams.get("sort_attr") || "",
        direction: searchParams.get("sort_order") || "",
    });

    const { practice } = useAppSelector((state) => state.userPractice);

    // Get filters from url
    const clientFilter = searchParams.get("search_string") || "";
    const providerNameFilter = searchParams.get("provider_name") || "";
    const authorFilter = searchParams.get("author") || "";
    const authorNameFilter = searchParams.get("author_name") || "";
    const cptCodeFilter = searchParams.get("cpt_code") || "";
    const insuranceProviderIdsFilter =
        getInsuranceProviderIdsFilter(searchParams);
    const noteTypeFilter = searchParams.getAll("type[]");
    const toDateFilter = searchParams.get("to_date");
    const showArchivedFilter = searchParams.get("show_archived") || "false";
    const fromDateCreatedFilter = searchParams.get("from_date_created");
    const toDateCreatedFilter = searchParams.get("to_date_created");
    const pageFilter = Number(searchParams.get("page")) || 1;
    const limitFilter =
        Number(searchParams.get("limit")) || LISTS_DEFAULT_LIMIT;

    const debouncedClienFilter = useDebounce(clientFilter);
    const debouncedProviderNameSearchString = useDebounce(
        providerNameSearchString
    );
    const debouncedAuthorSearchString = useDebounce(authorSearchString);

    const toDateFilterToDate = convertDateFilterStringToDate(toDateFilter);

    const fromDateCreatedFilterToDate = convertDateFilterStringToDate(
        fromDateCreatedFilter
    );
    const toDateCreatedFilterToDate =
        convertDateFilterStringToDate(toDateCreatedFilter);

    const handleResetProvidersSearchString = () => {
        setProviderNameSearchString("");
        setAuthorSearchString("");
    };

    const insuranceProviders = useFetchInsuranceProviders();

    const selectedInsuranceProviderNames = [
        {
            insurance_provider_id: "self_pay",
            alias: "Self pay",
        },
        ...(insuranceProviders?.data?.data || []),
    ]
        .filter((provider) =>
            insuranceProviderIdsFilter.includes(provider.insurance_provider_id)
        )
        .map((provider) => removeEnumUnderscore(provider.alias));

    const showInsOverSelfPay = useShowInsuranceOverSelfPay();

    const providers = useFetchProviders({
        search_string:
            debouncedProviderNameSearchString || debouncedAuthorSearchString,
        page: 1,
        limit: 20,
    });

    const handleDialogInView = (modal: Dialogs | null) => {
        setDialogInView(() => modal);
    };

    const sortableColumns = [
        "date_of_service",
        "client_name",
        "provider_name",
        "created_at",
        "author",
    ];

    const handleConfigAttribute = () => {
        if (sortableColumns.includes(sortConfig.key)) {
            return sortConfig.key as NoteListSortAttribute;
        }

        return undefined;
    };

    const notesFilters = {
        status: [NoteStatus.PENDING_CODE_REVIEW],
        tab_name: "all_notes",
        cpt_code: cptCodeFilter,
        type: noteTypeFilter,
        to_date: toDateFilter,
        from_created_at_date: fromDateCreatedFilter,
        to_created_at_date: toDateCreatedFilter,
        search_string: debouncedClienFilter,
        provider_name: providerNameFilter,
        page: pageFilter,
        insurance_provider_ids: insuranceProviderIdsFilter,
        limit: limitFilter,
        show_archived: showArchivedFilter,
        sort_attribute:
            searchParams.get("sort_attr") || handleConfigAttribute(),
        sort_order: searchParams.get("sort_order") || sortConfig.direction,
        author_id: authorFilter,
        flagged_for_audit_status: [FlaggedNoteForAuditStatus.UNFLAGGED],
    };

    const { data, isLoading, isSuccess, error } = useFetchNotesList(
        notesFilters,
        ""
    );
    const getAuthorTextToDisplay = getSelectedAuthorName(
        providers.data?.data,
        data?.data,
        authorFilter,
        authorNameFilter
    );

    /** FILTER INPUTS ONCHANGE FUNCTIONS */
    const handleSetClientNameFilter = (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        if (e.target.value === "") searchParams.delete("search_string");
        else searchParams.set("search_string", e.target.value);
        searchParams.set("page", "1");
        localStorage.setItem(LOCAL_STORAGE_KEY, searchParams.toString());
        setSearchParams(searchParams);
    };

    const handleSetAuthorFilter = (author: string, authorName = "") => {
        const nextSearchParams = updateAuthorFilterSearchParams(
            searchParams,
            author,
            authorName
        );
        localStorage.setItem(LOCAL_STORAGE_KEY, nextSearchParams.toString());
        setSearchParams(nextSearchParams);
    };

    const handleSetTypeFilter = (type: string) => {
        if (noteTypeFilter.includes(type)) {
            searchParams.delete("type[]", type);
        } else {
            searchParams.append("type[]", type);
        }
        searchParams.set("page", "1");
        localStorage.setItem(LOCAL_STORAGE_KEY, searchParams.toString());
        setSearchParams(searchParams);
    };

    const handleInsuranceProviderIdsChange = (
        insuranceProviderIds: string[]
    ) => {
        setInsuranceProviderIdsFilter(searchParams, insuranceProviderIds);
        searchParams.set("page", "1");
        localStorage.setItem(LOCAL_STORAGE_KEY, searchParams.toString());
        setSearchParams(searchParams);
    };

    const handleSetCptCodeFilter = (cptCode: string) => {
        if (cptCode === "") searchParams.delete("cpt_code");
        else searchParams.set("cpt_code", cptCode);
        searchParams.set("page", "1");
        localStorage.setItem(LOCAL_STORAGE_KEY, searchParams.toString());
        setSearchParams(searchParams);
    };

    const handleSetArchivedNotesFilter = (archived: string) => {
        if (archived === "") searchParams.delete("show_archived");
        else searchParams.set("show_archived", archived);
        searchParams.set("page", "1");
        localStorage.setItem(LOCAL_STORAGE_KEY, searchParams.toString());
        setSearchParams(searchParams);
    };

    const handleToDateFilterChange = (date: Date | null) => {
        if (date === null) searchParams.delete("to_date");
        else searchParams.set("to_date", format(date, "yyyy-MM-dd"));
        searchParams.set("page", "1");
        localStorage.setItem(LOCAL_STORAGE_KEY, searchParams.toString());
        setSearchParams(searchParams);
    };

    const handleFromDateCreatedFilterChange = (date: Date | null) => {
        if (date === null) searchParams.delete("from_date_created");
        else searchParams.set("from_date_created", format(date, "yyyy-MM-dd"));
        searchParams.set("page", "1");
        localStorage.setItem(LOCAL_STORAGE_KEY, searchParams.toString());
        setSearchParams(searchParams);
    };

    const handleToDateCreatedFilterChange = (date: Date | null) => {
        if (date === null) searchParams.delete("to_date_created");
        else searchParams.set("to_date_created", format(date, "yyyy-MM-dd"));
        searchParams.set("page", "1");
        localStorage.setItem(LOCAL_STORAGE_KEY, searchParams.toString());
        setSearchParams(searchParams);
    };

    const handleSetPageFilter = (page: number) => {
        searchParams.set("page", String(page));
        localStorage.setItem(LOCAL_STORAGE_KEY, searchParams.toString());
        setSearchParams(searchParams);
    };

    const handleSetLimitFilter = (limit: number) => {
        searchParams.set("limit", String(limit));
        searchParams.set("page", "1");
        localStorage.setItem(LOCAL_STORAGE_KEY, searchParams.toString());
        setSearchParams(searchParams);
    };

    const getArchivedNoteFilterToDisplay = () => {
        if (showArchivedFilter === "true") {
            return "Archived notes";
        }
        return "Unarchived notes";
    };

    const selectedIds = getSelectedData?.map(
        (item: Row<INote>) => item.original.note_id
    );

    // Array of details of the selected notes
    const detailsArray =
        selectedIds &&
        selectedIds.map((noteId: string) =>
            data?.data.find((note) => note.note_id === noteId)
        );

    // Details of a single note
    const noteDetails = data?.data.find((note) => {
        return note.note_id === codeToConfirmId;
    });

    const { toast } = useToast();

    const handleExportNotesCsv = async () => {
        setIsExportingCsv(true);
        try {
            await exportNotesCsv({
                ...notesFilters,
                note_ids: selectedIds,
            });
        } catch {
            toast({
                mode: "error",
                message: "Could not export notes at this time",
            });
        } finally {
            setIsExportingCsv(false);
        }
    };

    const queryClient = useQueryClient();

    const handleSuccess = () => {
        setRowSelection({});
    };

    // Confirm code mutation
    const confirmCodes = useChangeCptCode();

    const handleConfirmCodes = (noteId?: string) => {
        interface ConfirmCodePayload {
            [noteId: string]: {
                cpt_code: string;
                modifier_codes: string[];
                confirm_code: boolean;
            };
        }

        // Multi-selected notes
        const confirmCodesPayload: ConfirmCodePayload =
            detailsArray && detailsArray?.length !== 0
                ? detailsArray
                      .filter((note): note is INote => note !== undefined)
                      .reduce((payload: ConfirmCodePayload, note) => {
                          const noteIdForPayload = note.note_id;
                          return {
                              ...payload,
                              [noteIdForPayload]: {
                                  cpt_code: note.cpt_code || "",
                                  modifier_codes: note.modifier_codes || [],
                                  confirm_code: true,
                              },
                          };
                      }, {})
                : {};

        // Single note action
        const confirmCodePayload = {
            [String(noteId)]: {
                cpt_code: noteDetails?.cpt_code || "",
                modifier_codes: noteDetails?.modifier_codes || [],
                confirm_code: true,
            },
        };

        confirmCodes.mutate(
            detailsArray && detailsArray?.length !== 0
                ? confirmCodesPayload
                : confirmCodePayload,
            {
                onSuccess(res) {
                    queryClient.invalidateQueries({
                        queryKey: [`notes`],
                    });
                    toast({
                        mode: "success",
                        message:
                            res.message || "Cpt code confirmed successfully",
                    });
                    handleSuccess();
                },
                onError(err) {
                    toast({
                        mode: "error",
                        message:
                            err.response?.data.message ||
                            "Could not confirm code at this moment, please try again",
                    });
                },
            }
        );
    };

    // Unarchive mutation
    const unarchiveNote = useChangeNoteArchiveStatus();

    const archiveNoteIsMutating = useIsMutating(["archive-note"]);
    const flagNoteIsMutating = useIsMutating(["flag-note"]);
    const changeNoteIsMutating = useIsMutating(["change-code"]);

    const handleUnarchive = (id?: string) => {
        const noteIds = selectedIds || [];

        const dataToSend = {
            note_ids_to_archive: noteIds.length === 0 && id ? [id] : noteIds,
            archive_status: false,
        };

        unarchiveNote.mutate(dataToSend, {
            onSuccess: async (res) => {
                await queryClient.invalidateQueries({
                    queryKey: [`notes`],
                });

                toast({
                    mode: "success",
                    message: res.message || "Note unarchived successfully",
                });

                handleSuccess();
            },

            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Could not unarchive note at this time",
                });
            },
        });
    };

    // Export mutation
    const exportNotes = useExportNotes();

    const canExportNotes = useDoesUserHavePermission([
        UserPermisions.CLIENT_INFO_ALL_WRITE,
        UserPermisions.CLIENT_INFO_NOTES_WRITE,
        UserPermisions.CLIENT_INFO_ALL_ADMIN,
    ]);

    const handleExportNotes = (noteId?: string) => {
        const notesToExport = noteId
            ? data?.data.filter((note) => note.note_id === noteId) ?? []
            : getSelectedData?.map((item) => item.original) ?? [];
        const exportableNoteIds = getPdfExportableNoteIds(notesToExport);

        if (exportableNoteIds.length === 0) {
            toast({
                mode: "warning",
                message: "The selected notes are unavailable for PDF export",
            });
            return;
        }

        const dataToSend = { session_note_ids: exportableNoteIds };

        exportNotes.mutate(dataToSend, {
            onSuccess: async (res) => {
                handleDialogInView("loader");
                await new Promise<void>((resolve) => {
                    const timer = setTimeout(() => {
                        clearTimeout(timer);
                        setJobId(res.data.export_job_id);
                        setPdfCount(res.data.expected_pdf_count);
                        resolve();
                    }, 8000);
                });
            },
            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.message || "Could not export notes at this time",
                });
            },
        });
    };

    const exportNote = useExportPdfs(
        {
            export_job_id: jobId as string,
            pdf_type: "session_note",
            expected_pdf_count: pdfCount,
        },
        Boolean(jobId)
    );

    if (jobId && !exportNote.isLoading) {
        setJobId(null);
        handleDialogInView(null);
        setRowSelection({});
    }

    // Action permissions
    const showActions =
        practice?.roles?.includes(UserRole.CODER) ||
        practice?.roles?.includes(UserRole.SUPER_ADMIN);

    const canArchiveNote = [
        UserPermisions.CLIENT_NOTES_ALL_READ,
        UserPermisions.CLIENT_INFO_NOTES_WRITE,
    ].some((permission) => practice?.permissions?.includes(permission));

    const isBillerOrBillerAdmin = useDoesUserHaveRole([
        UserRole.BILLER,
        UserRole.BILLER_ADMIN,
    ]);

    const canArchiveNoteWithClaim = (note: INote) =>
        !note.claim_id || isBillerOrBillerAdmin;

    const selectedNotesHaveClaim = detailsArray?.some((note) =>
        Boolean(note?.claim_id)
    );

    const canArchiveSelectedNotesWithClaim = () =>
        !selectedNotesHaveClaim || isBillerOrBillerAdmin;

    const showArchiveOption = () => canArchiveNote;

    const showBulkArchiveBtn = () => canArchiveNote;

    const showFlagOption = (row: Row<INote>) =>
        row.original.flagged_for_audit_status !==
            FlaggedNoteForAuditStatus.FLAGGED &&
        practice?.permissions?.includes(
            UserPermisions.CLIENT_INFO_NOTES_FLAG_FOR_AUDIT_WRITE
        ) &&
        !practice?.roles?.includes(UserRole.AUDITOR_ADMIN);

    const showBulkFlagNotesBtn =
        practice?.permissions?.includes(
            UserPermisions.CLIENT_INFO_NOTES_FLAG_FOR_AUDIT_WRITE
        ) &&
        !practice?.roles?.includes(UserRole.AUDITOR_ADMIN) &&
        detailsArray?.every(
            (note) =>
                note?.flagged_for_audit_status !==
                FlaggedNoteForAuditStatus.FLAGGED
        );

    const columns: ColumnDef<INote>[] = React.useMemo(
        () => [
            {
                id: "select",
                header: ({ table }) => (
                    <Checkbox
                        handleChange={table.getToggleAllRowsSelectedHandler()}
                        indeterminate={table.getIsSomeRowsSelected()}
                        label=""
                        name="row"
                        value="row"
                        isChecked={table.getIsAllRowsSelected()}
                    />
                ),
                cell: ({ row }) => (
                    <Checkbox
                        handleChange={row.getToggleSelectedHandler()}
                        indeterminate={row.getIsSomeSelected()}
                        label=""
                        name="row"
                        value="row"
                        isChecked={row.getIsSelected()}
                    />
                ),
            },
            {
                accessorKey: "date_of_service",
                header: "DATE OF SERVICE",
                cell: ({ row }) =>
                    row.original.date_of_service
                        ? formatDate(row.original.date_of_service)
                        : "--",
            },
            {
                accessorKey: "note_type",
                header: "NOTE TYPE",
                cell: ({ row }) =>
                    getNoteTypeTextWithTag(row.original.note_type),
            },
            {
                accessorKey: "client_name",
                header: "CLIENT",
                cell: ({ row }) => (
                    <span className="capitalize">
                        {displayNameInRightFormat({
                            firstName: row.original.client?.first_name,
                            lastName: row.original.client?.last_name,
                        })}
                    </span>
                ),
            },
            {
                accessorKey: "cpt_code",
                header: "CPT CODE(S)",
                cell: ({ row }) =>
                    getCptCodeToDisplay({
                        noteType: row.original.note_type,
                        cptCode: row.original.cpt_code,
                        additionalCptCodes: row.original.additional_cpt_codes,
                        unitCalculation:
                            row.original?.note_metadata?.unit_calculation,
                    }),
            },
            {
                accessorKey: "external_reference_id",
                header: "CLAIM REF NO",
                cell: ({ row }) => row.original.external_reference_id ?? "--",
            },
            {
                accessorKey: "author",
                header: "CLINICIAN",
                cell: ({ row }) => (
                    <span className="capitalize">
                        {displayNameInRightFormat({
                            firstName: row.original.author?.first_name,
                            lastName: row.original.author?.last_name,
                        })}
                    </span>
                ),
            },
            {
                accessorKey: "created_at",
                header: "CREATED ON",
                cell: ({ row }) => formatDate(row.original.created_at),
            },
            {
                accessorKey: "status",
                header: "NOTE STATUS / FLAGGED STATUS",
                cell: ({ row }) => (
                    <div className="flex flex-col items-start gap-1">
                        {getNoteStatusTag(row.original.status as NoteStatus)}
                        {row.original.flagged_for_audit_status
                            ? getFlaggedNoteStatusTag(
                                  row.original.flagged_for_audit_status
                              )
                            : null}
                    </div>
                ),
            },
            {
                accessorKey: "client",
                header: "INSURANCE PROVIDER",
                cell: ({ row }) => (
                    <span
                        className={
                            row.original.client.self_pay && !showInsOverSelfPay
                                ? "normal-case"
                                : "uppercase"
                        }
                    >
                        {row.original.client.self_pay && !showInsOverSelfPay
                            ? "Self pay"
                            : (row.original.client.insurance_provider_name &&
                                  removeEnumUnderscore(
                                      row.original.client
                                          .insurance_provider_name || ""
                                  )) ||
                              "--"}
                    </span>
                ),
            },
            {
                accessorKey: "note_id",
                header: "",
                cell: ({ row }) => (
                    <Dropdown>
                        <DropdownTrigger asChild>
                            <MoreButton
                                onPointerDown={() => {
                                    setCodeToConfirmId(
                                        () => row.original.note_id
                                    );
                                }}
                            />
                        </DropdownTrigger>
                        <DropdownContent align="end" width="auto">
                            {showActions &&
                                row.original.flagged_for_audit_status !==
                                    FlaggedNoteForAuditStatus.FLAGGED &&
                                row.original.note_type !==
                                    NoteTypes.CANCELLATION_NOTE && (
                                    <>
                                        <DropdownItem
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleConfirmCodes(
                                                    row.original.note_id
                                                );
                                            }}
                                            disabled={confirmCodes.isLoading}
                                        >
                                            Confirm Code
                                        </DropdownItem>
                                        <DropdownItem
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDialogInView(
                                                    "change-code"
                                                );
                                            }}
                                            disabled={confirmCodes.isLoading}
                                        >
                                            Change Code
                                        </DropdownItem>
                                    </>
                                )}
                            {canExportNotes &&
                                isNotePdfExportable(row.original) && (
                                    <DropdownItem
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleExportNotes(
                                                row.original.note_id
                                            );
                                        }}
                                    >
                                        Export to pdf
                                    </DropdownItem>
                                )}
                            {showArchiveOption() &&
                                canArchiveNoteWithClaim(row.original) &&
                                row.original.is_archived === false && (
                                    <DropdownItem
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleDialogInView("archive-note");
                                        }}
                                    >
                                        Archive note
                                    </DropdownItem>
                                )}
                            {showArchiveOption() && row.original.is_archived && (
                                <DropdownItem
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleUnarchive(row.original.note_id);
                                    }}
                                >
                                    Unarchive note
                                </DropdownItem>
                            )}
                            {showFlagOption(row) && (
                                <DropdownItem
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleDialogInView("flag_note");
                                    }}
                                >
                                    Flag for audit
                                </DropdownItem>
                            )}
                        </DropdownContent>
                    </Dropdown>
                ),
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    React.useEffect(() => {
        if (setNotesCount) {
            if (error) setNotesCount("");
            else if (isLoading) setNotesCount("");
            else if (data && data.total_count > 0) {
                setNotesCount(String(data.total_count));
            } else if (data && data.data.length === 0) {
                setNotesCount("");
            }
        }
    }, [data, error, isLoading, setNotesCount]);

    const pageToRecover = data
        ? getNotesRecoveryPage({
              count: data.count,
              currentPage: pageFilter,
              totalCount: data.total_count,
              totalPages: data.total_pages,
          })
        : null;

    React.useEffect(() => {
        if (pageToRecover) {
            const nextSearchParams = new URLSearchParams(searchParams);
            nextSearchParams.set("page", String(pageToRecover));
            localStorage.setItem(
                LOCAL_STORAGE_KEY,
                nextSearchParams.toString()
            );
            setSearchParams(nextSearchParams, { replace: true });
        }
    }, [pageToRecover, searchParams, setSearchParams]);

    React.useEffect(() => {
        searchParams.set("sort_attr", sortConfig.key);
        searchParams.set("sort_order", sortConfig.direction);
        localStorage.setItem(LOCAL_STORAGE_KEY, searchParams.toString());
        setSearchParams(searchParams);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sortConfig]);

    return (
        <>
            <Dialog open={dialogInView === "change-code"}>
                <DialogContent
                    title="Change code"
                    handleCloseDialog={() => handleDialogInView(null)}
                    showFooter
                    variant="center"
                    cancelText="Cancel"
                    saveText="Change code"
                    submitBtnFormValue="change-cpt-code"
                    isSubmitBtnDisabled={changeNoteIsMutating > 0}
                    isCancelBtnDisabled={changeNoteIsMutating > 0}
                >
                    {isPsychNote(noteDetails?.note_type) ? (
                        <ChangeCodeFormPsych
                            onFormSubmit={() => {
                                handleDialogInView(null);
                                handleSuccess();
                            }}
                            cptCode={noteDetails?.cpt_code || ""}
                            notes={detailsArray as INote[]}
                            showDetails={false}
                            note={noteDetails}
                        />
                    ) : (
                        <ChangeCode
                            note={noteDetails}
                            onFormSubmit={() => {
                                handleDialogInView(null);
                                handleSuccess();
                            }}
                            showDetails={false}
                            notes={detailsArray as INote[]}
                        />
                    )}
                </DialogContent>
            </Dialog>
            <Dialog open={dialogInView === "archive-note"}>
                <DialogContent
                    title="Archive Note"
                    variant="center"
                    showFooter
                    saveText="Archive Note"
                    submitBtnFormValue="archive-note"
                    handleCloseDialog={() => handleDialogInView(null)}
                    isSubmitBtnDisabled={archiveNoteIsMutating > 0}
                    isCancelBtnDisabled={archiveNoteIsMutating > 0}
                >
                    <ArchiveNote
                        onFormSubmit={() => {
                            handleDialogInView(null);
                            handleSuccess();
                        }}
                        noteId={noteDetails?.note_id as string}
                        selectedIds={selectedIds}
                    />
                </DialogContent>
            </Dialog>
            <Dialog
                open={
                    dialogInView === "flag_note" ||
                    dialogInView === "unflag_note"
                }
            >
                <DialogContent
                    title={
                        dialogInView === "flag_note"
                            ? "Flag for Audit"
                            : "Unflag Note for Audit"
                    }
                    variant="center"
                    showFooter
                    saveText={
                        dialogInView === "flag_note"
                            ? "Flag for Audit"
                            : "Unflag Note for Audit"
                    }
                    submitBtnFormValue="flag-note"
                    handleCloseDialog={() => {
                        handleDialogInView(null);
                        setRowSelection({});
                    }}
                    isSubmitBtnDisabled={flagNoteIsMutating > 0}
                    isCancelBtnDisabled={flagNoteIsMutating > 0}
                >
                    <FlagNoteForm
                        onFormSubmit={() => {
                            handleDialogInView(null);
                            handleSuccess();
                        }}
                        noteId={noteDetails?.note_id}
                        selectedIds={selectedIds}
                        action={
                            dialogInView === "flag_note" ? "flag" : "unflag"
                        }
                    />
                </DialogContent>
            </Dialog>
            <Dialog open={dialogInView === "loader"}>
                <DialogContent
                    title="Export in progress"
                    variant="center"
                    handleCloseDialog={() => handleDialogInView(null)}
                    showFooter={false}
                >
                    <ExportLoaderAndText />
                </DialogContent>
            </Dialog>
            <Navbar
                title={`Pending Code Review${
                    notesCount ? ` (${notesCount})` : ""
                }`}
            />
            <div className="page">
                <Card classNames="mb-[--spacing-header]">
                    <div className="flex flex-col gap-[--spacing-items]">
                        <div className="flex items-center justify-between flex-wrap gap-[--spacing-items]">
                            <SearchInput
                                defaultValue={clientFilter}
                                onChange={handleSetClientNameFilter}
                                containerClass="w-[400px] max-w-full"
                                placeholder="Search by client name or chart number"
                            />
                        </div>
                        <div className="flex items-center justify-between flex-wrap gap-[--spacing-items]">
                            <div className="flex items-center gap-[--spacing-items] flex-wrap">
                                <Dropdown>
                                    <DropdownTrigger asChild>
                                        <FilterButton>Note type</FilterButton>
                                    </DropdownTrigger>
                                    <DropdownContent align="end" width="auto">
                                        {Object.values(NoteTypes).map(
                                            (type) => (
                                                <DropdownItem
                                                    key={type}
                                                    onClick={() =>
                                                        handleSetTypeFilter(
                                                            type
                                                        )
                                                    }
                                                    isMulti
                                                    itemValue={type}
                                                    values={noteTypeFilter}
                                                >
                                                    {makeStringFirstLetterCapital(
                                                        removeEnumUnderscore(
                                                            NoteLabels[type]
                                                        )
                                                    )}
                                                </DropdownItem>
                                            )
                                        )}
                                    </DropdownContent>
                                </Dropdown>

                                <InsuranceProviderFilter
                                    values={insuranceProviderIdsFilter}
                                    onChange={handleInsuranceProviderIdsChange}
                                />

                                <Dropdown>
                                    <DropdownTrigger asChild>
                                        <FilterButton className="normal-case">
                                            {cptCodeFilter || "CPT code"}
                                        </FilterButton>
                                    </DropdownTrigger>
                                    <DropdownContent>
                                        <DropdownItem
                                            isSelected={!cptCodeFilter}
                                            onClick={() =>
                                                handleSetCptCodeFilter("")
                                            }
                                        >
                                            All
                                        </DropdownItem>
                                        {CPT_CODES.map((code) => (
                                            <DropdownItem
                                                key={code}
                                                isSelected={
                                                    cptCodeFilter === code
                                                }
                                                onClick={() =>
                                                    handleSetCptCodeFilter(code)
                                                }
                                            >
                                                {removeEnumUnderscore(code)}
                                            </DropdownItem>
                                        ))}
                                    </DropdownContent>
                                </Dropdown>

                                <Popover
                                    open={showAuthorPopover}
                                    onOpenChange={(open) => {
                                        setShowAuthorPopover(open);
                                        handleResetProvidersSearchString();
                                    }}
                                >
                                    <PopoverTrigger
                                        asChild
                                        onClick={() =>
                                            setShowAuthorPopover(true)
                                        }
                                    >
                                        <FilterButton
                                            className={
                                                authorFilter
                                                    ? "capitalize"
                                                    : "normal-case"
                                            }
                                        >
                                            {getAuthorTextToDisplay
                                                ? truncateString(
                                                      getAuthorTextToDisplay
                                                  )
                                                : "Clinician"}
                                        </FilterButton>
                                    </PopoverTrigger>
                                    <PopoverContent align="start">
                                        <SearchInput
                                            placeholder="Search by name"
                                            onChange={(e) =>
                                                setAuthorSearchString(
                                                    e.target.value
                                                )
                                            }
                                        />

                                        <div className="flex flex-col items-start max-h-[230px] overflow-y-auto">
                                            {providers.isLoading ? (
                                                <Loader wrapperClassName="h-[150px]" />
                                            ) : null}
                                            {providers.error && (
                                                <ListState
                                                    isError
                                                    context="general"
                                                    stateHelperText="Please try again later"
                                                    errorMsg={
                                                        providers.error
                                                            ?.response?.data
                                                            ?.message ||
                                                        "Error loading authors"
                                                    }
                                                    cardClass="h-[150px]"
                                                />
                                            )}
                                            {providers.data?.data &&
                                                providers.isSuccess && (
                                                    <>
                                                        <Button
                                                            size="auto"
                                                            variant="can-hover"
                                                            className="font-medium capitalize w-full justify-start my-1.5 shrink-0"
                                                            onClick={() => {
                                                                handleSetAuthorFilter(
                                                                    ""
                                                                );
                                                                setShowAuthorPopover(
                                                                    false
                                                                );
                                                                handleResetProvidersSearchString();
                                                            }}
                                                        >
                                                            All
                                                        </Button>
                                                        {providers?.data?.data.map(
                                                            (provider) => (
                                                                <Button
                                                                    key={
                                                                        provider.provider_id
                                                                    }
                                                                    size="auto"
                                                                    variant="can-hover"
                                                                    className="font-medium capitalize w-full justify-start mb-1.5 shrink-0"
                                                                    onClick={() => {
                                                                        handleSetAuthorFilter(
                                                                            provider.user_id,
                                                                            provider.provider_name
                                                                        );
                                                                        setShowAuthorPopover(
                                                                            false
                                                                        );
                                                                        handleResetProvidersSearchString();
                                                                    }}
                                                                >
                                                                    {displayNameInRightFormat(
                                                                        provider.provider_name
                                                                    )}
                                                                    {authorFilter ===
                                                                    provider.user_id ? (
                                                                        <CheckPrimaryColorIcon />
                                                                    ) : null}
                                                                </Button>
                                                            )
                                                        )}
                                                    </>
                                                )}
                                        </div>
                                    </PopoverContent>
                                </Popover>

                                <Dropdown>
                                    <DropdownTrigger asChild>
                                        <FilterButton className="normal-case">
                                            {getArchivedNoteFilterToDisplay()}
                                        </FilterButton>
                                    </DropdownTrigger>
                                    <DropdownContent>
                                        <DropdownItem
                                            isSelected={
                                                showArchivedFilter === "true"
                                            }
                                            onClick={() =>
                                                handleSetArchivedNotesFilter(
                                                    "true"
                                                )
                                            }
                                        >
                                            Archived notes
                                        </DropdownItem>
                                        <DropdownItem
                                            isSelected={
                                                showArchivedFilter === "false"
                                            }
                                            onClick={() =>
                                                handleSetArchivedNotesFilter(
                                                    "false"
                                                )
                                            }
                                        >
                                            Unarchived notes
                                        </DropdownItem>
                                    </DropdownContent>
                                </Dropdown>
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <FilterButton>
                                            Date Created
                                        </FilterButton>
                                    </PopoverTrigger>
                                    <PopoverContent align="end">
                                        <div className="mb-1">
                                            <DatePicker
                                                label="From"
                                                placeholderText="MM/DD/YYYY"
                                                selected={
                                                    fromDateCreatedFilterToDate
                                                }
                                                onChange={
                                                    handleFromDateCreatedFilterChange
                                                }
                                                maxDate={
                                                    toDateCreatedFilterToDate ||
                                                    new Date(Date.now())
                                                }
                                                isClearable
                                            />
                                        </div>
                                        <DatePicker
                                            label="To"
                                            placeholderText="MM/DD/YYYY"
                                            selected={toDateCreatedFilterToDate}
                                            onChange={
                                                handleToDateCreatedFilterChange
                                            }
                                            minDate={
                                                fromDateCreatedFilterToDate
                                            }
                                            isClearable
                                        />
                                    </PopoverContent>
                                </Popover>
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <FilterButton>
                                            End Date Of Service
                                        </FilterButton>
                                    </PopoverTrigger>
                                    <PopoverContent align="end">
                                        <DatePicker
                                            label="To DOS"
                                            placeholderText="MM/DD/YYYY"
                                            selected={toDateFilterToDate}
                                            onChange={handleToDateFilterChange}
                                            isClearable
                                        />
                                    </PopoverContent>
                                </Popover>
                            </div>
                            <Button
                                onClick={handleExportNotesCsv}
                                disabled={isExportingCsv || !data?.total_count}
                            >
                                Export to CSV
                            </Button>
                        </div>
                    </div>
                    <TableFiltersDisplay
                        appliedFilters={[
                            {
                                key: "type[]",
                                values: noteTypeFilter.map(
                                    (type) => NoteLabels[type]
                                ),
                            },
                            {
                                key: "to_date",
                                values: toDateFilter || "",
                            },
                            {
                                key: searchParams.has(
                                    "insurance_provider_ids[]"
                                )
                                    ? "insurance_provider_ids[]"
                                    : "insurance_provider",
                                label: "Insurance",
                                preserveValues: true,
                                values: selectedInsuranceProviderNames,
                            },
                            {
                                key: "cpt_code",
                                values: cptCodeFilter || "",
                            },
                            {
                                key: "author",
                                label: "Clinician",
                                preserveValues: true,
                                values: getAuthorTextToDisplay
                                    ? [getAuthorTextToDisplay]
                                    : [],
                            },
                            {
                                key: "show_archived",
                                values:
                                    showArchivedFilter === "true"
                                        ? "Archived notes"
                                        : "",
                            },
                            {
                                key: "from_date_created",
                                values: fromDateCreatedFilter || "",
                            },
                            {
                                key: "to_date_created",
                                values: toDateCreatedFilter || "",
                            },
                        ]}
                        wrapperClass="mt-[--spacing-items]"
                        localStorageKey={LOCAL_STORAGE_KEY}
                    />
                </Card>

                {isLoading || pageToRecover ? <Loader /> : null}
                {error && (
                    <ListState
                        isError
                        stateHelperText="Try reloading this page to solve this issue"
                        errorMsg={
                            error?.response?.data.message ||
                            `Cannot display notes list at this time please try again later`
                        }
                    />
                )}
                {data &&
                    isSuccess &&
                    data.data.length === 0 &&
                    data.total_count === 0 && (
                        <ListState
                            stateHelperText="Notes pending code review will appear here"
                            isError={false}
                            emptyMessage="No pending code review notes found"
                        />
                    )}

                {isSuccess && data && data.data.length > 0 && (
                    <>
                        {getTotalAmountOfTableRowsSelected(rowSelection)
                            .amount > 0 && (
                            <div className="flex items-center mb-[--spacing-items] gap-[--spacing-items] flex-wrap">
                                <div className="font-medium text-base shrink-0">
                                    {
                                        getTotalAmountOfTableRowsSelected(
                                            rowSelection
                                        ).amountString
                                    }
                                    :
                                </div>

                                {showBulkArchiveBtn() &&
                                    detailsArray?.every(
                                        (note) => note?.is_archived === false
                                    ) &&
                                    canArchiveSelectedNotesWithClaim() && (
                                        <Button
                                            variant="secondary"
                                            onClick={() =>
                                                handleDialogInView(
                                                    "archive-note"
                                                )
                                            }
                                        >
                                            Archive note
                                        </Button>
                                    )}

                                {showBulkArchiveBtn() &&
                                    detailsArray?.every(
                                        (note) => note?.is_archived
                                    ) && (
                                        <Button
                                            variant="secondary"
                                            onClick={() => handleUnarchive()}
                                        >
                                            Unarchive note
                                        </Button>
                                    )}

                                {showBulkFlagNotesBtn && (
                                    <Button
                                        variant="secondary"
                                        onClick={() =>
                                            handleDialogInView("flag_note")
                                        }
                                    >
                                        Flag for audit
                                    </Button>
                                )}
                            </div>
                        )}
                        <div>
                            <Table
                                columns={columns}
                                data={data.data}
                                rowSelection={rowSelection}
                                setRowSelection={setRowSelection}
                                handleRowClick={(row) => {
                                    navigate(
                                        `/notes/${row.original.client.client_id}/${row.original.note_id}?viewFrom=session_note`
                                    );
                                }}
                                setGetSelectedData={setGetSelectedData}
                                className={styles.table}
                                setSortConfig={setSortConfig}
                                sortConfig={sortConfig}
                                sortableColumns={sortableColumns}
                                hasPagination={data.total_count > 20}
                                pagination={
                                    <Pagination
                                        totalCount={data.total_count}
                                        totalPages={data.total_pages}
                                        currentPage={pageFilter}
                                        onPageChange={handleSetPageFilter}
                                        limit={limitFilter}
                                        onLimitChange={handleSetLimitFilter}
                                    />
                                }
                            />
                        </div>
                    </>
                )}
            </div>
        </>
    );
}
